import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
    Zap, Crosshair, Users, CheckCircle,
    BarChart, Smartphone, Loader, ArrowRight,
    Star, ChevronRight, MessageCircle, Activity, 
    BrainCircuit, Layout, Cpu, ShieldCheck
} from 'lucide-react';
import { fetchPublicPlans } from '../redux/plansSlice';
import { BrandLink } from '../components/BrandLogo';
import Seo from '../components/Seo';
import WebsiteFooter from '../components/WebsiteFooter';
import { cn } from '../utils/cn';
import { formatCurrency } from '../utils/currency';

// Public marketing page — no logged-in tenant to read a currency setting
// from; plan prices are always advertised in the platform's own currency.
const PLATFORM_CURRENCY = 'INR';

// ── Animated counter ────────────────────────────────────────────────────────
function CountUp({ end, suffix = '', decimals = 0, inView }) {
    const [val, setVal] = useState(0);
    useEffect(() => {
        if (!inView) return;
        let cur = 0; const step = end / (1800 / 16);
        const t = setInterval(() => { cur = Math.min(cur + step, end); setVal(cur); if (cur >= end) clearInterval(t); }, 16);
        return () => clearInterval(t);
    }, [inView, end]);
    return <>{decimals ? val.toFixed(decimals) : Math.floor(val)}{suffix}</>;
}

// ── Scroll reveal hook ────────────────────────────────────────────────────────
function useReveal() {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.12 });
        if (ref.current) obs.observe(ref.current); return () => obs.disconnect();
    }, []);
    return [ref, visible];
}

const FEATURES = [
    { Icon: Crosshair, color: 'text-[#0176D3]', title: 'Meta Ads Management', desc: 'Create and orchestrate official Facebook and Instagram campaigns with automated audience targeting via Meta Marketing API.' },
    { Icon: MessageCircle, color: 'text-[#04844B]', title: 'WhatsApp Business Automation', desc: 'Official WhatsApp Business Platform integration for message templates, auto-replies, and trigger-based customer engagement.' },
    { Icon: Layout, color: 'text-[#DD7A01]', title: 'AI-Powered Unified CRM', desc: 'Industrial-grade Lead, Contact, and Opportunity management with a visual Kanban pipeline board for total lifecycle tracking.' },

    { Icon: BrainCircuit, color: 'text-[#9050E9]', title: 'AI-Driven Automation Engine', desc: 'Orchestrate complex customer journeys with logic-based triggers (IF Lead Created → Send WhatsApp Confirmation).' },
    { Icon: Activity, color: 'text-[#0176D3]', title: 'Real-time Reporting Hub', desc: 'Deep analytics and telemetry for campaign performance, ad spend ROI, and WhatsApp engagement across all nodes.' },
    { Icon: ShieldCheck, color: 'text-[#BA0517]', title: 'Enterprise-Grade Security', desc: 'Bank-grade E2EE, JWT isolation, and full multi-tenant data protection for industrial-scale reliability and compliance.' },
];


const STATS = [
    { Icon: MessageCircle, end: 10, suffix: 'M+', label: 'Signals Delivered' },
    { Icon: Zap, end: 99.9, suffix: '%', label: 'Operational Uptime', decimals: 1 },
    { Icon: Users, end: 5000, suffix: '+', label: 'Active Matrices' },
    { Icon: Star, end: 24, suffix: '/7', label: 'Support Uptime' },
];

const LandingPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { isAuthenticated } = useSelector(s => s.auth);
    const { plans, loading: plansLoading } = useSelector(s => s.plans);
    const [statsRef, statsInView] = useReveal();
    const [featuresRef, featuresInView] = useReveal();
    const [pricingRef, pricingInView] = useReveal();
    const [ctaRef, ctaInView] = useReveal();


    useEffect(() => { dispatch(fetchPublicPlans()); }, [dispatch]);

    const fmtPrice = p => p === 0 ? 'Free' : formatCurrency(p, PLATFORM_CURRENCY);
    const fmtLimit = n => n >= 1_000_000 ? 'Unlimited' : n >= 1000 ? `${(n / 1000).toFixed(0)}K` : n;
    const popularIdx = plans.length > 1 ? Math.floor(plans.length / 2) : -1;

    return (
        <div className="min-h-screen bg-white text-[var(--slds-color-text-main)] overflow-x-hidden selection:bg-[var(--slds-color-brand)]/20 font-sans">
            <Seo
                title="WaapLeads CRM | AI CRM, WhatsApp Business Automation & Meta Ads Platform"
                description="Grow with WaapLeads CRM: AI-powered lead management, WhatsApp Business automation, Meta Ads campaign control, and customer engagement analytics in one platform."
                pathname="/"
                keywords={[
                    'AI CRM platform',
                    'WhatsApp Business API CRM',
                    'Meta Ads campaign management',
                    'lead tracking software',
                    'customer engagement platform',
                    'marketing automation software',
                    'sales CRM India',
                    'WaapLeads CRM',
                ]}
                structuredData={{
                    '@context': 'https://schema.org',
                    '@type': 'Organization',
                    name: 'WaapLeads CRM',
                    url: window.location.origin,
                    logo: `${window.location.origin}/branding/waapleads-logo.svg?v=2`,
                    sameAs: ['https://wa.me/917392080372'],
                }}
            />
            
            {/* ── Navbar ── */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-[var(--slds-color-border)] h-14 shadow-sm">
                <div className="w-full px-6 lg:px-20 h-full flex items-center justify-between">
                    <div onClick={() => navigate('/')}>
                        <BrandLink to="/" size={36} subtitle="AI Revenue Command" />
                    </div>
                    
                    <div className="hidden md:flex items-center gap-8">
                        <a href="#features" className="text-[13px] font-semibold hover:text-[var(--slds-color-brand)] transition-colors">Solutions</a>
                        <a href="#pricing" className="text-[13px] font-semibold hover:text-[var(--slds-color-brand)] transition-colors">Pricing</a>
                        <Link to="/contact" className="text-[13px] font-semibold hover:text-[var(--slds-color-brand)] transition-colors">Company</Link>
                    </div>

                    <div className="flex items-center gap-4">
                        {isAuthenticated ? (
                            <Link to="/dashboard" className="slds-button-brand h-8 px-4 text-[12px] flex items-center justify-center">Dashboard</Link>
                        ) : (
                            <>
                                <Link to="/login" className="text-[13px] font-semibold px-3">Login</Link>
                                <Link to="/register" className="slds-button-brand h-8 px-4 text-[12px] flex items-center justify-center">Try for Free</Link>
                            </>
                        )}
                    </div>
                </div>
            </nav>

            {/* ── Hero ── */}
            <section className="pt-32 pb-24 px-6 lg:px-20 bg-[var(--slds-color-bg)] border-b border-[var(--slds-color-border)] flex flex-col items-start text-left">
                <div className="w-full space-y-8">
                    <div className="px-4 py-1.5 rounded-full bg-white border border-[var(--slds-color-border)] text-[12px] font-bold text-[var(--slds-color-brand)] inline-flex items-center gap-2 shadow-sm">
                        <Zap size={14} /> AI CRM + WhatsApp Business Automation + Meta Ads
                    </div>
                    <h1 className="text-4xl lg:text-6xl font-bold tracking-tight leading-tight">
                        Manage Leads, Campaigns, and Customer Conversations<br /> <span className="text-[var(--slds-color-brand)]">from One AI-Powered Revenue Platform</span>
                    </h1>
                    <p className="text-[18px] font-medium text-[var(--slds-color-text-secondary)] leading-relaxed max-w-3xl">
                        WaapLeads CRM brings together WhatsApp Business messaging, Meta Ads operations, AI CRM workflows, lead pipelines, and real-time analytics so sales and marketing teams can move faster without losing visibility.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                        <Link to="/register" className="slds-button-brand h-10 px-10 text-[14px] flex items-center justify-center">Start Free Trial</Link>
                        <a href="#features" className="slds-button-secondary h-10 px-8 text-[14px] flex items-center justify-center gap-2">
                           Explore Core Features <ChevronRight size={16} />
                        </a>
                    </div>
                </div>
            </section>

            {/* ── Trust Stats ── */}
            <section className="py-20 px-6 lg:px-20 bg-white grid grid-cols-2 lg:grid-cols-4 gap-12" ref={statsRef}>
                {STATS.map(({ Icon, end, suffix, label, decimals }, i) => (
                    <div key={i} className="flex flex-col items-center text-center space-y-4">
                        <div className="w-12 h-12 rounded-[var(--slds-radius)] bg-[var(--slds-color-bg)] flex items-center justify-center text-[var(--slds-color-brand)] border border-[var(--slds-color-border)]">
                            <Icon size={20} strokeWidth={2} />
                        </div>
                        <div className="space-y-0.5">
                            <h3 className="text-3xl font-bold text-[var(--slds-color-text-main)] tracking-tight">
                                <CountUp end={end} suffix={suffix} decimals={decimals} inView={statsInView} />
                            </h3>
                            <p className="text-[12px] font-semibold text-[var(--slds-color-text-secondary)] uppercase tracking-wide">{label}</p>
                        </div>
                    </div>
                ))}
            </section>

            {/* ── Features ── */}
            <section id="features" className="py-24 px-6 lg:px-20 bg-[var(--slds-color-bg)] border-y border-[var(--slds-color-border)]" ref={featuresRef}>
                <div className="text-left space-y-4 mb-20">
                    <span className="text-[12px] font-bold text-[var(--slds-color-brand)] uppercase tracking-widest">Enterprise Platform</span>
                    <h2 className="text-3xl font-bold">Search-Friendly Revenue Operations Infrastructure</h2>
                    <p className="text-[15px] font-medium text-[var(--slds-color-text-secondary)] max-w-2xl">Build, scale, and automate customer acquisition, messaging, and CRM workflows with one operational layer.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {FEATURES.map(({ Icon, title, desc }, i) => (
                        <div key={i} className="card group hover:shadow-[0_2px_8px_rgba(0,0,0,0.16)] transition-all p-8 flex flex-col items-start bg-white border border-[var(--slds-color-border)] rounded-[var(--slds-radius)]">
                            <div className="w-10 h-10 rounded-[var(--slds-radius)] bg-[var(--slds-color-bg)] border border-[var(--slds-color-border)] flex items-center justify-center mb-6 text-[var(--slds-color-brand)] transition-transform group-hover:rotate-6">
                               <Icon size={22} strokeWidth={2} />
                            </div>
                            <h3 className="text-[18px] font-bold text-[var(--slds-color-text-main)] mb-3">{title}</h3>
                            <p className="text-[13px] font-medium text-[var(--slds-color-text-secondary)] leading-relaxed">{desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── Pricing ── */}
            <section id="pricing" className="py-24 px-6 lg:px-20 bg-white" ref={pricingRef}>
                <div className="text-left space-y-4 mb-20">
                    <h2 className="text-3xl font-bold">Transparent Professional Plans</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {plansLoading ? (
                        <div className="col-span-full py-20 flex justify-center"><Loader className="animate-spin text-[var(--slds-color-brand)]" /></div>
                    ) : plans.map((plan, idx) => (
                        <div key={plan._id} className={cn(
                            "card p-10 flex flex-col bg-white border border-[var(--slds-color-border)] rounded-[var(--slds-radius)]",
                            idx === popularIdx && "ring-2 ring-[var(--slds-color-brand)] border-transparent"
                        )}>
                            <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                            <div className="mb-6">
                                <span className="text-4xl font-bold">{fmtPrice(plan.price)}</span>
                                {plan.price > 0 && <span className="text-sm text-[var(--slds-color-text-secondary)] ml-1">/mo</span>}
                            </div>
                            <ul className="flex-1 space-y-4 mb-10">
                                <li className="flex items-center gap-3 text-[13px] font-medium text-[var(--slds-color-text-secondary)]">
                                    <CheckCircle size={16} className="text-[#04844B]" /> {plan.maxDevices} Active Channels
                                </li>
                                <li className="flex items-center gap-3 text-[13px] font-medium text-[var(--slds-color-text-secondary)]">
                                    <CheckCircle size={16} className="text-[#04844B]" /> {fmtLimit(plan.monthlyMessageLimit)} Messages/mo
                                </li>
                            </ul>
                            <Link to="/register" className={idx === popularIdx ? "slds-button-brand h-10 flex items-center justify-center p-0" : "slds-button-secondary h-10 flex items-center justify-center p-0"}>
                                Get Started
                            </Link>
                        </div>
                    ))}
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="py-24 px-6 lg:px-20 bg-[var(--slds-color-bg)] border-t border-[var(--slds-color-border)] text-left" ref={ctaRef}>
                <div className="w-full space-y-10">
                    <h2 className="text-4xl font-bold">Ready to Elevate Your Customer Engagement?</h2>
                    <p className="text-[18px] font-medium text-[var(--slds-color-text-secondary)]">Join 5,000+ businesses optimizing their communication stream on WaapLeads CRM.</p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link to="/register" className="slds-button-brand h-12 px-12 text-[16px] flex items-center justify-center">Start Your Free Trial</Link>
                        <Link to="/contact" className="slds-button-secondary h-12 px-12 text-[16px] flex items-center justify-center">Talk to Our Team</Link>
                    </div>
                </div>
            </section>

            <WebsiteFooter />
        </div>
    );
};

export default LandingPage;
